import type { SelectOption } from "@/design-system/components";

const FALLBACK_TIMEZONE = "Asia/Kolkata";

const FALLBACK_TIMEZONE_OPTIONS: SelectOption[] = [
  { value: "UTC", label: "UTC (GMT+0)" },
  { value: "America/New_York", label: "America/New York (GMT-5)" },
  { value: "America/Los_Angeles", label: "America/Los Angeles (GMT-8)" },
  { value: "Europe/London", label: "Europe/London (GMT+0)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (GMT+5:30)" },
];

function getSupportedTimezones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return FALLBACK_TIMEZONE_OPTIONS.map((option) => String(option.value));
  }
}

export function isSupportedTimezone(
  timezone: string | null | undefined,
): timezone is string {
  if (!timezone) return false;
  return getSupportedTimezones().includes(timezone);
}

export function getDeviceTimezone(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isSupportedTimezone(timezone) ? timezone : FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

export function normalizeTimezone(
  timezone: string | null | undefined,
): string {
  return isSupportedTimezone(timezone) ? timezone : getDeviceTimezone();
}

function getTimezoneOffsetLabel(timezone: string, now: Date): string {
  try {
    const offsetPart = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName");
    return offsetPart?.value ?? "";
  } catch {
    return "";
  }
}

export function formatTimezoneLabel(timezone: string, now: Date = new Date()): string {
  const name = timezone.replace(/_/g, " ");
  const offset = getTimezoneOffsetLabel(timezone, now);
  return offset ? `${name} (${offset})` : name;
}

export function getTimezoneOptions(): SelectOption[] {
  try {
    const timezones = Intl.supportedValuesOf("timeZone");
    const now = new Date();
    return timezones.map((tz) => ({
      value: tz,
      label: formatTimezoneLabel(tz, now),
    }));
  } catch {
    return FALLBACK_TIMEZONE_OPTIONS;
  }
}

/**
 * Timezone options guaranteed to include `current`, even when it is a non-canonical
 * IANA alias (e.g. "Asia/Calcutta") that `Intl.supportedValuesOf` omits. Without
 * this, an Ant `Select` bound to such a value would render the raw identifier with
 * no offset label and break search filtering. The current value is surfaced first.
 */
export function getTimezoneOptionsWith(
  current: string | null | undefined,
): SelectOption[] {
  const options = getTimezoneOptions();
  const value = current?.trim();
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }
  return [{ value, label: formatTimezoneLabel(value) }, ...options];
}
